import axios from 'axios';
import { API_BASE_URL } from '../config/constants';

// تعریف وضعیت‌های ممکن برای درخواست ویرایش فروشگاه
export enum StoreEditRequestStatus {
  PENDING = 'pending',     // در انتظار بررسی
  APPROVED = 'approved',   // تایید شده
  REJECTED = 'rejected',   // رد شده
  CANCELLED = 'cancelled'  // لغو شده توسط فروشنده
}

// تعریف اینترفیس برای مدل درخواست ویرایش فروشگاه
export interface IStoreEditRequest {
  _id: string;
  storeId: {
    _id: string;
    name: string;
    slug?: string;
    logo?: string;
  };
  originalData: any;
  editData: any;
  status: StoreEditRequestStatus;
  approvedBy?: {
    _id: string;
    firstName?: string;
    lastName?: string;
    email?: string;
  };
  approvedAt?: Date;
  approvalNote?: string;
  rejectedBy?: {
    _id: string;
    firstName?: string;
    lastName?: string;
    email?: string;
  };
  rejectedAt?: Date;
  rejectionReason?: string;
  createdBy: {
    _id: string;
    firstName?: string;
    lastName?: string;
    email?: string;
  };
  createdAt: Date;
  updatedAt: Date;
}

export interface StoreEditRequestResponse {
  success: boolean;
  message: string;
  data: {
    docs: IStoreEditRequest[];
    totalDocs: number;
    limit: number;
    page: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

export interface SingleStoreEditRequestResponse {
  success: boolean;
  message: string;
  data: IStoreEditRequest;
}

class StoreEditRequestService {
  private getAuthHeaders() {
    const token = localStorage.getItem('accessToken');
    return {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    };
  }

  /**
   * دریافت تمام درخواست‌های ویرایش فروشگاه
   */
  async getAllEditRequests(
    page: number = 1,
    limit: number = 10,
    status?: StoreEditRequestStatus
  ): Promise<StoreEditRequestResponse> {
    try {
      let url = `${API_BASE_URL}/admin/store-edit-requests?page=${page}&limit=${limit}`;
      if (status) {
        url += `&status=${status}`;
      }

      const response = await axios.get(url, this.getAuthHeaders());
      return response.data;
    } catch (error: any) {
      console.error('Error fetching store edit requests:', error);
      throw error;
    }
  }

  /**
   * ✅ نسخهٔ پارامتری — برای فیلتر بر اساس storeId (مثلاً برای تاریخچهٔ یک فروشگاه)
   */
  async getEditRequests(opts: {
    page?: number;
    limit?: number;
    status?: StoreEditRequestStatus;
    storeId?: string;
  }): Promise<StoreEditRequestResponse> {
    try {
      const params = new URLSearchParams();
      params.set('page', String(opts.page || 1));
      params.set('limit', String(opts.limit || 10));
      if (opts.status) params.set('status', opts.status);
      if (opts.storeId) params.set('storeId', opts.storeId);
      const response = await axios.get(
        `${API_BASE_URL}/admin/store-edit-requests?${params.toString()}`,
        this.getAuthHeaders()
      );
      return response.data;
    } catch (error: any) {
      console.error('Error fetching store edit requests:', error);
      throw error;
    }
  }

  /**
   * دریافت جزئیات درخواست ویرایش
   */
  async getEditRequestDetails(requestId: string): Promise<SingleStoreEditRequestResponse> {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/admin/store-edit-requests/${requestId}`,
        this.getAuthHeaders()
      );
      return response.data;
    } catch (error: any) {
      console.error('Error fetching edit request details:', error);
      throw error;
    }
  }

  /**
   * تایید درخواست ویرایش
   */
  async approveEditRequest(requestId: string, approvalNote?: string): Promise<SingleStoreEditRequestResponse> {
    try {
      const response = await axios.patch(
        `${API_BASE_URL}/admin/store-edit-requests/${requestId}/approve`,
        { approvalNote },
        this.getAuthHeaders()
      );
      return response.data;
    } catch (error: any) {
      console.error('Error approving edit request:', error);
      throw error;
    }
  }

  /**
   * رد درخواست ویرایش
   */
  async rejectEditRequest(requestId: string, rejectionReason: string): Promise<SingleStoreEditRequestResponse> {
    try {
      const response = await axios.patch(
        `${API_BASE_URL}/admin/store-edit-requests/${requestId}/reject`,
        { rejectionReason },
        this.getAuthHeaders()
      );
      return response.data;
    } catch (error: any) {
      console.error('Error rejecting edit request:', error);
      throw error;
    }
  }

  /**
   * دریافت آمار درخواست‌های ویرایش
   */
  async getEditRequestStats(): Promise<any> {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/admin/store-edit-requests/stats`,
        this.getAuthHeaders()
      );
      return response.data;
    } catch (error: any) {
      console.error('Error fetching edit request stats:', error);
      throw error;
    }
  }
}

export default new StoreEditRequestService(); 